import InfiniteScroll from 'react-infinite-scroll-component';

import { Flex, Grid, GridItem, Spinner, Text } from '@tradener/lumen';

import { CreditCounterpartyProps } from '@graphql/query/operationLimits/credit/counterpartList';

import { CreditSortDirectionProps, CreditSortFieldProps } from '../../types/OperationLimitsDashboard';
import CounterpartyListEmpty from '../CounterpartyListEmpty';
import CounterpartyListItem from '../CounterpartyListItem';
import CounterpartyListSkeleton from '../CounterpartyListSkeleton';

export interface CounterpartyListProps {
  counterparties: CreditCounterpartyProps[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  sortField: CreditSortFieldProps;
  sortDirection: CreditSortDirectionProps;
  onSort: (field: CreditSortFieldProps) => void;
  onLoadMore: () => Promise<void>;
  onOpenDetail: (accountId: string, counterpartyName: string) => void;
}

const sortLabel = (field: CreditSortFieldProps, sortField: CreditSortFieldProps, sortDirection: CreditSortDirectionProps) => {
  if (field !== sortField) {
    return '';
  }

  return sortDirection === 'DESC' ? ' ↓' : ' ↑';
};

const GRID_TEMPLATE_COLUMNS = [
  'minmax(0, 2.2fr)',
  'minmax(0, 1.1fr)',
  'minmax(0, 1.1fr)',
  'minmax(11rem, 1.2fr)',
  'minmax(11rem, 1.2fr)',
  'minmax(9rem, 1fr)',
  'minmax(9rem, 1fr)',
].join(' ');

function CounterpartyList({
  counterparties,
  loading,
  loadingMore,
  hasMore,
  sortField,
  sortDirection,
  onSort,
  onLoadMore,
  onOpenDetail,
}: CounterpartyListProps) {
  if (!loading && counterparties.length === 0) {
    return <CounterpartyListEmpty />;
  }

  return (
    <Flex direction="column" width="full" overflowX="hidden">
      <InfiniteScroll
        dataLength={counterparties.length}
        next={onLoadMore}
        hasMore={hasMore}
        style={{ overflow: 'hidden' }}
        loader={
          loadingMore ? (
            <Flex align="center" justify="center" py="5">
              <Spinner color="orange.500" />
            </Flex>
          ) : null
        }
      >
        <Grid gridTemplateColumns={GRID_TEMPLATE_COLUMNS} width="full" maxW="full">
          <GridItem py="3" px="3" borderBottom="1px solid" borderColor="gray.100" display="flex" alignItems="center">
            <Text
              as="button"
              textStyle="caption1-medium"
              color="gray.500"
              cursor="pointer"
              whiteSpace="nowrap"
              onClick={() => onSort('COUNTERPARTY')}
              title="Ordenar por contraparte"
            >
              {`Conta/CNPJ${sortLabel('COUNTERPARTY', sortField, sortDirection) || ' ↕'}`}
            </Text>
          </GridItem>
          <GridItem py="3" px="3" borderBottom="1px solid" borderColor="gray.100" display="flex" alignItems="center">
            <Text textStyle="caption1-medium" color="gray.500">
              Classificação
            </Text>
          </GridItem>
          <GridItem py="3" px="3" borderBottom="1px solid" borderColor="gray.100" display="flex" alignItems="center">
            <Text textStyle="caption1-medium" color="gray.500">
              Prazo de Negociação
            </Text>
          </GridItem>
          <GridItem
            py="3"
            px="3"
            borderBottom="1px solid"
            borderColor="gray.100"
            textAlign="right"
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Text
              as="button"
              textStyle="caption1-medium"
              color="gray.500"
              whiteSpace="nowrap"
              onClick={() => onSort('EXPOSURE')}
              title="Ordenar por exposição"
            >
              {`Exposição MtM (R$)${sortLabel('EXPOSURE', sortField, sortDirection)}`}
            </Text>
          </GridItem>
          <GridItem
            py="3"
            px="3"
            borderBottom="1px solid"
            borderColor="gray.100"
            textAlign="right"
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Text
              as="button"
              textStyle="caption1-medium"
              color="gray.500"
              whiteSpace="nowrap"
              onClick={() => onSort('EXPOSURE_GLOBAL')}
              title="Ordenar por exposição global"
            >
              {`Exposição MtM global (R$)${sortLabel('EXPOSURE_GLOBAL', sortField, sortDirection)}`}
            </Text>
          </GridItem>
          <GridItem
            py="3"
            px="3"
            borderBottom="1px solid"
            borderColor="gray.100"
            textAlign="right"
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Text textStyle="caption1-medium" color="gray.500" whiteSpace="nowrap">
              Limite
            </Text>
          </GridItem>
          <GridItem
            py="3"
            px="3"
            borderBottom="1px solid"
            borderColor="gray.100"
            textAlign="right"
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Text
              as="button"
              textStyle="caption1-medium"
              color="gray.500"
              onClick={() => onSort('BALANCE')}
              title="Ordenar por saldo"
            >
              {`Saldo financeiro${sortLabel('BALANCE', sortField, sortDirection)}`}
            </Text>
          </GridItem>
          {loading ? (
            <CounterpartyListSkeleton />
          ) : (
            counterparties.map((item) => <CounterpartyListItem key={item.accountId} item={item} onOpenDetail={onOpenDetail} />)
          )}
        </Grid>
      </InfiniteScroll>
    </Flex>
  );
}

export default CounterpartyList;
